import { useCallback, useEffect, useRef, useState } from 'react';

import { ArrowPathIcon, ArrowUturnLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { PercentCrop, PixelCrop } from 'react-image-crop';
import { ReactCrop, convertToPixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import Button from '~/local/components/button';
import { centerAspectCrop } from '~/local/custom-puzzle/image-upload';
import { canvasPreview } from '~/local/custom-puzzle/image-upload/canvasPreview';
import '~/local/custom-puzzle/image-upload/style.css';
import { useDebounceEffect } from '~/local/custom-puzzle/image-upload/useDebounceEffect';
import { cn } from '~/styling';

interface CropEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  initialCrop: PercentCrop;
  puzzleWidth: number;
  puzzleHeight: number;
  /** Show the crop as a circle (the saved image is still its square) */
  circular?: boolean;
  onSave: (crop: PercentCrop, croppedBlob: Blob) => void;
}

export function CropEditorModal({
  isOpen,
  onClose,
  imageUrl,
  initialCrop,
  puzzleWidth,
  puzzleHeight,
  circular = false,
  onSave,
}: CropEditorModalProps) {
  const [crop, setCrop] = useState<PercentCrop>(initialCrop);
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [aspect, setAspect] = useState(puzzleHeight / puzzleWidth);

  const imgRef = useRef<HTMLImageElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const croppedBlobRef = useRef<Blob | null>(null);

  // Handle browser back button to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handlePopState = (e: PopStateEvent) => {
      e.preventDefault();
      onClose();
    };

    // Push a new state when modal opens
    window.history.pushState({ modal: true }, '');
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height, naturalWidth, naturalHeight } = e.currentTarget;

    // Determine if aspect should be rotated
    const imageAspect = naturalWidth / naturalHeight;
    const currentAspectDiff = Math.abs(imageAspect - aspect);
    const rotatedAspectDiff = Math.abs(imageAspect - 1 / aspect);

    let newAspect = aspect;
    if (rotatedAspectDiff < currentAspectDiff) {
      newAspect = 1 / aspect;
    }

    setAspect(newAspect);

    // Set initial crop or use provided one
    if (initialCrop.width > 0) {
      setCrop(initialCrop);
      setCompletedCrop(convertToPixelCrop(initialCrop, width, height));
    } else {
      const newCrop = centerAspectCrop(width, height, newAspect);
      setCrop(newCrop);
      setCompletedCrop(convertToPixelCrop(newCrop, width, height));
    }
  }

  function onAspectRotate() {
    const newAspect = 1 / aspect;
    setAspect(newAspect);

    if (!imgRef.current) return;

    const { width, height } = imgRef.current;
    const newCrop = centerAspectCrop(width, height, newAspect);

    setCrop(newCrop);
    setCompletedCrop(convertToPixelCrop(newCrop, width, height));
  }

  function resetCrop() {
    if (!imgRef.current) return;

    const { width, height } = imgRef.current;
    const newCrop = centerAspectCrop(width, height, aspect);

    setCrop(newCrop);
    setCompletedCrop(convertToPixelCrop(newCrop, width, height));
  }

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        await canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop);

        previewCanvasRef.current.toBlob(
          (blob) => {
            if (blob) {
              croppedBlobRef.current = blob;
            }
          },
          'image/png',
          1
        );
      }
    },
    100,
    [completedCrop]
  );

  const handleSave = useCallback(() => {
    if (croppedBlobRef.current && crop) {
      onSave(crop, croppedBlobRef.current);
      onClose();
    }
  }, [crop, onSave, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
      <div className="relative h-full w-full overflow-auto bg-white p-4 sm:p-6 md:p-8">
        <div className="mx-auto max-w-6xl">
          {/* Header */}
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-xl font-medium text-primary-2">Edit Crop</h2>
            <button
              onClick={onClose}
              className="rounded-full p-2 text-neutral-2-80 hover:bg-neutral-2-10"
              aria-label="Close"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Controls */}
          <div className="mb-4 flex justify-end gap-2">
            {!circular && (
              <Button
                variant={{ intent: 'tertiary' }}
                className="flex items-center gap-1"
                type="button"
                onClick={onAspectRotate}
              >
                <ArrowPathIcon className="w-4" />
                <span>Rotate</span>
              </Button>
            )}
            <Button
              variant={{ intent: 'tertiary' }}
              className="flex items-center gap-1"
              type="button"
              onClick={resetCrop}
            >
              <ArrowUturnLeftIcon className="w-4" />
              <span>Reset</span>
            </Button>
          </div>

          {/* Crop area */}
          <div className="flex justify-center">
            <ReactCrop
              className={cn('inline-flex max-h-[70vh]')}
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspect}
              circularCrop={circular}
            >
              <img
                ref={imgRef}
                alt="Crop me"
                src={imageUrl}
                crossOrigin="anonymous"
                onLoad={onImageLoad}
                style={{ maxHeight: '70vh', maxWidth: '100%', display: 'block' }}
              />
            </ReactCrop>
          </div>

          {/* Hidden preview canvas */}
          {!!completedCrop && <canvas ref={previewCanvasRef} className="hidden" />}

          {/* Action buttons */}
          <div className="mt-6 flex justify-center gap-4">
            <Button variant={{ intent: 'tertiary' }} onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save Crop</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
